import dayjs from 'dayjs';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { QStack, QText, useQToast } from '@captivateiq/quotient';


import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';

const formatTimeAgo = (datetime: string): string => {
  if (!dayjs(datetime).isValid()) {
    return 'Invalid datetime format';
  }

  const targetDatetime = dayjs(datetime);
  const now = dayjs();
  const diffYears = now.diff(targetDatetime, 'year');
  if (diffYears > 0) {
    return `${diffYears}yr`;
  }

  const diffMonths = now.diff(targetDatetime, 'month');
  if (diffMonths > 0) {
    return `${diffMonths}mo`;
  }

  const diffDays = now.diff(targetDatetime, 'day');
  if (diffDays > 0) {
    return `${diffDays}d`;
  }

  const diffHours = now.diff(targetDatetime, 'hour');
  if (diffHours > 0) {
    return `${diffHours}h`;
  }

  return 'now';
};

type HistoryEntryProps = {
  id: string;
  title: string;
  lastUpdated: string;
};

export const HistoryEntry = ({ id, title, lastUpdated }: HistoryEntryProps) => {
  const { t } = useTranslation('assistant');
  const { error: errorToast } = useQToast();
  const updatedTimeAgo = useMemo(() => formatTimeAgo(lastUpdated), [lastUpdated]);

  const { openConversation } = useAIShellContext();

  const handleOpenConversation = useCallback(async () => {
    try {
      await openConversation(id);
    } catch (error) {
      errorToast({
        description: t('messages.error_loading_message_history'),
        autoHideDuration: 5000,
        isClosable: true,
      });
    }
  }, [openConversation, id, errorToast, t]);

  return (
    <QStack
      direction="row"
      sx={{
        alignItems: 'center',
        cursor: 'pointer',
        justifyContent: 'space-between',
        paddingX: '12px',
        paddingY: '8px',
        width: '100%',
        '&:hover': {
          backgroundColor: 'grey.100',
        },
      }}
      onClick={handleOpenConversation}
    >
      <QText sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>{title}</QText>
      <QText>{updatedTimeAgo}</QText>
    </QStack>
  );
};
